import type { IPascoRepository } from "@src/domain/pascos/repository/pasco_repository";
import type { PascoDTO, UpdatePascoDTO } from "../dto";
import { PascoDtoMapper } from "../mappers/pasco-dto.mapper";
import { Pasco } from "@src/domain/pascos/entities/pasco.entity";
import { AcademicYear } from "@src/domain/pascos/value_objects/academic_year";
import { UniqueEntityID } from "@src/domain/pascos/value_objects/unique_entity_Id";

/**
 * Use Case: Update PASCO
 * Updates an existing PASCO record
 */
export class UpdatePascoUseCase {
    constructor(private readonly pascoRepository: IPascoRepository) { }

    /**
     * Execute the use case
     * @param updateData - The update data containing the PASCO ID and fields to update
     * @returns Promise<PascoDTO> - The updated PASCO DTO
     * @throws Error if validation fails or PASCO not found
     */
    async execute(updateData: UpdatePascoDTO): Promise<PascoDTO> {
        if (!updateData.id) {
            throw new Error('PASCO ID is required for update');
        }

        // Get the existing PASCO
        const existingPasco = await this.pascoRepository.getById(updateData.id);
        if (!existingPasco) {
            throw new Error(`PASCO with ID ${updateData.id} not found`);
        }

        // Helper functions for clean null/undefined handling
        const updateRequired = <T>(newValue: T | null | undefined, existingValue: T): T => {
            return newValue != null ? newValue : existingValue;
        };

        const updateOptional = <T>(newValue: T | null | undefined, existingValue: T | null | undefined): T | null | undefined => {
            return newValue !== undefined ? newValue : existingValue;
        };

        // Create updated PASCO with merged data
        const updatedPasco = Pasco.create(
            {
                path: updateRequired(updateData.path, existingPasco.path),
                courseCode: updateRequired(updateData.courseCode, existingPasco.courseCode),
                courseName: updateRequired(updateData.courseName, existingPasco.courseName),
                academicYear: updateRequired(updateData.academicYear, existingPasco.academicYear)
                    ? AcademicYear.create(updateData.academicYear!)
                    : AcademicYear.create(existingPasco.academicYear!),
                semester: updateRequired(updateData.semester, existingPasco.semester),
                level: updateRequired(updateData.level, existingPasco.level),
                isApproved: updateRequired(updateData.isApproved, existingPasco.isApproved),
                isReviewed: updateRequired(updateData.isReviewed, existingPasco.isReviewed),
                isInterimAssessment: updateRequired(updateData.isInterimAssessment, existingPasco.isInterimAssessment),
                isSupplementary: updateRequired(updateData.isSupplementary, existingPasco.isSupplementary),
                processingStatus: existingPasco.processingStatus,
                courseDescription: updateOptional(updateData.courseDescription, existingPasco.courseDescription),
                department: updateOptional(updateData.department, existingPasco.department),
                universityId: updateRequired(updateData.universityId, existingPasco.universityId),
                createdAt: existingPasco.createdAt,
                updatedAt: new Date(),
            },
            new UniqueEntityID(updateData.id)
        );

        const savedPasco = await this.pascoRepository.update(updatedPasco);
        return PascoDtoMapper.toDTO(savedPasco);
    }
}
